import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CalendarClock, ChevronDown, Copy, Loader2, BellRing, Trash2 } from "lucide-react";
import { ConfirmDialog } from "../../components/ConfirmDialog";

const WINDOW_TYPES = ["review", "consultation", "approval"];
const typeLabel = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const border = { borderColor: "#E5E7EB" };
const fieldCls = "rounded-lg px-2.5 py-1.5 outline-none border focus:ring-2 focus:ring-[#F97316] text-xs";

function copyText(text, ok) {
  navigator.clipboard.writeText(text)
    .then(() => toast.success(ok))
    .catch(() => toast.error("Copy blocked — select the text and copy manually"));
}

function pillStyle(w) {
  if (w.state !== "open") return { backgroundColor: "#F3F4F6", color: "#6B7280" };
  if (w.within_final_2) return { backgroundColor: "#FEF3C7", color: "#B45309" };
  return { backgroundColor: "#DCFCE7", color: "#10B981" };
}

function pillLabel(w) {
  if (w.state !== "open") return "Closed";
  return w.within_final_2 ? "Open · closing soon" : "Open";
}

function closeInfo(w) {
  if (w.state === "open") {
    if (w.indefinite) return "No close date (indefinite)";
    return `Closes 5:00pm ${new Date(w.closes_at).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" })} · ${w.remaining_business_days} business day(s) left`;
  }
  const d = w.closed_at || w.closes_at;
  return d ? `Closed ${new Date(d).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}` : "Not opened yet";
}

function useOpenWindow({ clientId, reload, onNotification }) {
  const [busy, setBusy] = useState(false);
  const open = async ({ type, category, duration, includeChanges, onDone }) => {
    const days = duration.trim() === "" ? null : parseInt(duration, 10);
    if (duration.trim() !== "" && (!Number.isInteger(days) || days < 1)) {
      toast.error("Duration must be a whole number of business days, or blank for indefinite");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/windows`, {
        type, category, duration_days: days, include_changes: includeChanges, open_now: true,
      });
      onNotification({ text: data.notification_text });
      toast.success(`${data.label} ${type} window opened`);
      onDone();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open window");
    } finally { setBusy(false); }
  };
  return { busy, open };
}

function WindowSelectors({ type, setType, category, setCategory, categories }) {
  return (
    <>
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1 block">Type</label>
        <select data-testid="window-type-select" value={type} onChange={(e) => setType(e.target.value)} className={fieldCls} style={border}>
          {WINDOW_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1 block">Category</label>
        <select data-testid="window-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={fieldCls} style={border}>
          {categories.map((c) => <option key={c.category} value={c.category}>{c.label} ({c.doc_count})</option>)}
        </select>
      </div>
    </>
  );
}

function CreateWindowForm({ clientId, categories, reload, onNotification }) {
  const [type, setType] = useState("review");
  const [category, setCategory] = useState(categories[0]?.category || "");
  const [duration, setDuration] = useState("");
  const [includeChanges, setIncludeChanges] = useState(true);
  const { busy, open } = useOpenWindow({ clientId, reload, onNotification });

  const create = () => open({ type, category, duration, includeChanges, onDone: () => setDuration("") });

  return (
    <div className="mx-4 my-3 flex flex-wrap items-end gap-2 rounded-lg border p-3" style={border} data-testid="window-create-form">
      <WindowSelectors type={type} setType={setType} category={category} setCategory={setCategory} categories={categories} />
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1 block">Business days</label>
        <input data-testid="window-create-duration" value={duration} onChange={(e) => setDuration(e.target.value)}
          placeholder="Blank = open" className={`${fieldCls} w-24`} style={border} />
      </div>
      <label className="flex items-center gap-1.5 text-xs text-[#6B7280] pb-1.5 cursor-pointer select-none">
        <input data-testid="window-include-changes" type="checkbox" checked={includeChanges}
          onChange={(e) => setIncludeChanges(e.target.checked)} className="accent-[#F97316]" />
        Included changes end at close
      </label>
      <button data-testid="window-create-btn" onClick={create} disabled={busy}
        className="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
        style={{ backgroundColor: "#F97316" }}>
        {busy && <Loader2 size={12} className="animate-spin" />} Open window
      </button>
    </div>
  );
}

function useWindowActions(w, reload, onNotification) {
  const [duration, setDuration] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const reopen = async () => {
    const days = duration.trim() === "" ? null : parseInt(duration, 10);
    if (duration.trim() !== "" && (!Number.isInteger(days) || days < 1)) {
      toast.error("Duration must be a whole number of business days, or blank for indefinite");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/windows/${w.id}/open`, { duration_days: days });
      onNotification({ text: data.notification_text });
      toast.success(`${w.label} ${w.type} window reopened`);
      setDuration("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen window");
    } finally { setBusy(false); }
  };

  const closeWindow = async () => {
    setBusy(true);
    try {
      await api.post(`/windows/${w.id}/close`);
      toast.success(`${w.label} ${w.type} window closed`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close window");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setConfirmDel(false);
    try {
      await api.delete(`/windows/${w.id}`);
      toast.success("Window deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete window");
    }
  };

  return { duration, setDuration, busy, confirmDel, setConfirmDel, reopen, closeWindow, remove };
}

function WindowRow({ w, reload, onNotification }) {
  const { duration, setDuration, busy, confirmDel, setConfirmDel, reopen, closeWindow, remove } = useWindowActions(w, reload, onNotification);

  return (
    <div className="border-b last:border-b-0" style={{ borderColor: "#F3F4F6" }} data-testid={`window-row-${w.id}`}>
      <div className="grid grid-cols-12 items-center gap-2 px-4 py-3">
        <div className="col-span-3">
          <p className="font-medium text-sm">{w.label}</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: "#E8EDF4", color: "#0A1F44" }} data-testid={`window-type-${w.id}`}>{typeLabel(w.type)}</span>
          {w.include_changes && (
            <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>Included changes</span>
          )}
        </div>
        <div className="col-span-1 text-sm text-[#6B7280]" data-testid={`window-doccount-${w.id}`}>{w.doc_count}</div>
        <div className="col-span-2">
          <span data-testid={`window-state-${w.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={pillStyle(w)}>
            {pillLabel(w)}
          </span>
        </div>
        <div className="col-span-3 text-xs text-[#6B7280]" data-testid={`window-closeinfo-${w.id}`}>{closeInfo(w)}</div>
        <div className="col-span-3 flex items-center justify-end gap-1.5">
          {w.state === "open" ? (
            <button data-testid={`window-close-${w.id}`} onClick={closeWindow} disabled={busy}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border text-[#6B7280] hover:border-red-400 hover:text-red-500 disabled:opacity-60"
              style={border}>
              Close early
            </button>
          ) : (
            <>
              <input data-testid={`window-duration-${w.id}`} value={duration} onChange={(e) => setDuration(e.target.value)}
                placeholder="Days" className="w-16 rounded-lg px-2 py-1.5 text-xs border outline-none focus:ring-2 focus:ring-[#F97316]"
                style={border} title="Business days — leave blank for an indefinite window" />
              <button data-testid={`window-reopen-${w.id}`} onClick={reopen} disabled={busy}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
                Reopen
              </button>
              <button data-testid={`window-delete-${w.id}`} onClick={() => setConfirmDel(true)}
                className="p-1.5 rounded hover:bg-gray-100 text-red-400"><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </div>
      {w.reminder_text && (
        <div className="mx-4 mb-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs"
          style={{ backgroundColor: "#FEF3C7", color: "#B45309" }} data-testid={`window-reminder-${w.id}`}>
          <span className="flex items-center gap-1.5"><BellRing size={13} /> {w.reminder_text}</span>
          <button data-testid={`copy-reminder-${w.id}`} onClick={() => copyText(w.reminder_text, "Reminder text copied")}
            className="flex items-center gap-1 font-semibold flex-shrink-0"><Copy size={12} /> Copy</button>
        </div>
      )}
      <ConfirmDialog open={confirmDel} message={`Delete this ${w.type} window? Its history will be removed from this panel.`}
        confirmLabel="Delete" onConfirm={remove} onCancel={() => setConfirmDel(false)} testId={`window-delete-confirm-${w.id}`} />
    </div>
  );
}

export default function ReviewWindows({ clientId }) {
  const [data, setData] = useState({ windows: [], categories: [] });
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState(null);

  const load = useCallback(() => api.get(`/clients/${clientId}/windows`).then(({ data }) => setData(data)), [clientId]);
  useEffect(() => { load(); setNotif(null); }, [load]);

  const openCount = data.windows.filter((w) => w.state === "open").length;

  return (
    <div className="rounded-xl border bg-white mb-4 overflow-hidden" style={border} data-testid="review-windows-panel">
      <button data-testid="review-windows-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5">
        <span className="font-heading font-semibold flex items-center gap-2 text-sm">
          <CalendarClock size={16} style={{ color: "#F97316" }} /> Windows
          <span className="text-xs font-normal text-[#6B7280]">({openCount} open)</span>
        </span>
        <ChevronDown size={18} className={`text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t" style={border}>
          <p className="px-4 pt-3 pb-1 text-xs text-[#6B7280]">
            Review, Consultation and Approval windows per category — one open window per type per category at a time.
            Durations are in <span className="font-semibold">business days following the Victorian public holiday calendar</span>;
            windows close at 5:00pm on the final business day. Leave duration blank for an indefinite window.
            When "Included changes end at close" is ticked, feedback logged after the close date is flagged post-window.
          </p>

          <CreateWindowForm clientId={clientId} categories={data.categories} reload={load} onNotification={setNotif} />

          {notif && (
            <div className="mx-4 my-3 rounded-lg border p-3" style={{ backgroundColor: "#FEF0E6", borderColor: "#FDBA74" }} data-testid="window-notification">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#9A3412" }}>
                  Client notification — copy into your email
                </p>
                <button data-testid="copy-notification" onClick={() => copyText(notif.text, "Notification copied")}
                  className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#F97316" }}><Copy size={12} /> Copy</button>
              </div>
              <p className="text-sm" style={{ color: "#9A3412" }} data-testid="window-notification-text">{notif.text}</p>
            </div>
          )}

          {data.windows.length > 0 && (
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] border-b" style={{ borderColor: "#F3F4F6" }}>
              <div className="col-span-3">Category</div>
              <div className="col-span-1">Docs</div>
              <div className="col-span-2">State</div>
              <div className="col-span-3">Close</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
          )}
          {data.windows.length === 0 && (
            <p className="px-4 py-4 text-sm text-[#9CA3AF]" data-testid="windows-empty">No windows yet — open one above.</p>
          )}
          {data.windows.map((w) => (
            <WindowRow key={w.id} w={w} reload={load} onNotification={setNotif} />
          ))}
        </div>
      )}
    </div>
  );
}
